import { computed } from 'vue';
import { storeToRefs } from 'pinia';

import { useAuthStore } from '@/stores/auth.store';
import { usePusherStore } from '@/stores/pusher.store';
import { EntityTypeEnum, RoleEventEnum } from '@/types/plugins/pusher.types';
import {
  getRoleEventDialogDetails,
  revalidateRouteAfterRoleChange,
} from '@/common/utils/pusher.utils';

/**
 * The live role-change dialog's derived state and its single dismissal path — the header close
 * and the Dismiss button both go through `dismiss`, so neither can skip the route revalidation.
 */
export const useRoleEvent = () => {
  const authStore = useAuthStore();
  const pusherStore = usePusherStore();

  const { loggedInUser } = storeToRefs(authStore);
  const { roleMessage } = storeToRefs(pusherStore);

  // Context: logout resets the auth store to a null user while this dialog is still mounted.
  const organisations = computed(() => loggedInUser.value?.organisations ?? []);

  // A single org-role removal leaves the user as an org member (default ORG_MEMB), so the
  // removed org is still in their refreshed org list. That distinguishes "lost a role"
  // (retain access — neutral message, no switcher) from actual org removal (org gone).
  const retainsOrgAccess = computed(
    () =>
      roleMessage.value.EntityType === EntityTypeEnum.ORG &&
      roleMessage.value.EventType === RoleEventEnum.REMOVED &&
      organisations.value.some((org) => org.id === roleMessage.value.OrgId),
  );

  const isOrgRemoval = computed(
    () =>
      roleMessage.value.EntityType === EntityTypeEnum.ORG &&
      roleMessage.value.EventType === RoleEventEnum.REMOVED &&
      !retainsOrgAccess.value,
  );

  const showOrganisationPicker = computed(
    () => isOrgRemoval.value && organisations.value.length > 0,
  );

  const details = computed(() =>
    getRoleEventDialogDetails(
      roleMessage.value.EventType,
      roleMessage.value.EntityType,
      retainsOrgAccess.value,
    ),
  );

  // Close, then re-evaluate the current route against the rehydrated abilities so a live role
  // change takes effect immediately (no refresh) — see revalidateRouteAfterRoleChange.
  const dismiss = async () => {
    pusherStore.showRoleEventModal(false);
    await revalidateRouteAfterRoleChange();
  };

  return {
    details,
    organisations,
    retainsOrgAccess,
    isOrgRemoval,
    showOrganisationPicker,
    dismiss,
  };
};
